import { PauseSet } from './PauseSet';
import { Fragment } from '@tiptap/pm/model';
import { EditorState } from '@tiptap/pm/state';
import { Editor } from '@tiptap/react';
import { ExternalStoreSynchroniserOptions, SaveTiptapEditorContentArgs } from './externalStoreSynchroniserTypes';
import { isTiptapEditorContentFragmentEmpty } from '../../utils/isTiptapEditorEmpty';
import { getLineCount } from '../../utils/getLineCount';
import { haveTaskListLabelIdsChanged } from '../../utils/taskItemLabelIdUtils';
import { IGNORE_UNDO_REDO_TRANSACTION_ID } from '../../../../client/utils/undoRedo/undoRedoConstants';

/**
 * An implementation of the storage for the ExternalStoreSynchroniser.
 * This is a class to simplify the management of the state of the extension.
 *
 * NOTE: This class heavily uses `this` internally. As such it must be referenced like `storage.methodName()`
 *  rather than `const { methodName } = storage`.
 */
export class ExternalStoreSynchroniserStorage {
    // The options that the extension was created with
    private options: ExternalStoreSynchroniserOptions;

    // A set of consumers that wish for updates to be paused
    private pausers = new PauseSet();
    private timeoutId = 0;
    private unsyncedUpdateCount = 0;

    // The last content that was stored in the external store
    private lastStoredContent: Fragment | null = null;

    constructor(options: ExternalStoreSynchroniserOptions) {
        this.options = options;
    }

    /**
     * If the content has changed externally to this class, allow the updated
     * content to be referenced here.
     */
    public setLastStoredContent(value: Fragment | null) {
        this.lastStoredContent = value;
    }

    /**
     * Prevents flushing of updates to the external store.
     */
    public pauseUpdates(key: string) {
        this.pausers.pauseUpdates(key);
    }

    /**
     * Resumes flushing of updates to the external store.
     */
    public resumeUpdates(key: string) {
        this.pausers.resumeUpdates(key);
    }

    /**
     * Gets the current state of the editor and sends it to the external store, if:
     * - There are unsynced updates
     * - The syncing is not paused
     * - The content has changed since the last sync
     */
    public flushUpdate({
        transactionId,
        actionOrigin,
        additionalChanges,
        state,
        editor,
    }: SaveTiptapEditorContentArgs & {
        state?: EditorState;
        editor: Editor;
    }) {
        if (!this.lastStoredContent) return;

        if (this.unsyncedUpdateCount === 0) return;

        if (this.pausers.isPaused()) return;

        const updatedDoc = state?.doc ?? editor.state.doc;

        // No new changes, so ignore update
        if (updatedDoc.content.eq(this.lastStoredContent)) return;

        // When the content initialises, it won't have node keys, so the first update will be simply initialising
        //  the keys. This doesn't result in any visual changes for the user, so shouldn't get added to the app history.
        const shouldNotAddToAppHistory =
            !transactionId &&
            isTiptapEditorContentFragmentEmpty(this.lastStoredContent) &&
            isTiptapEditorContentFragmentEmpty(updatedDoc.content);

        const updateTransactionId = shouldNotAddToAppHistory ? IGNORE_UNDO_REDO_TRANSACTION_ID : transactionId;

        this.unsyncedUpdateCount = 0;
        this.lastStoredContent = updatedDoc.content;

        this.options.onDebouncedUpdate(updatedDoc.toJSON(), {
            transactionId: updateTransactionId,
            actionOrigin,
            // Only included when there are some - this is used to allow a single flushed update rather than a
            //  text content update followed by another element content update.
            // E.g. Acknowledging a suggestion & adding the title in a single ELEMENT_UPDATE action.
            // This will avoid a potential race condition if two actions arrive in quick succession on the server
            ...(additionalChanges ? { additionalChanges } : {}),
        });
    }

    /**
     * Waits until either the debounce time has passed or the debounce count has been reached before
     * triggering an update.
     */
    public debounceUpdate({ editor }: { editor: Editor }) {
        if (!this.lastStoredContent) return;

        if (this.timeoutId) clearTimeout(this.timeoutId);

        // Preserve the pending update while paused so the owner can resume and flush it
        // with an explicit transaction ID, without allowing an intermediate save.
        this.unsyncedUpdateCount++;
        if (this.pausers.isPaused()) return;

        const previousContent = this.lastStoredContent;
        const currentContent = editor.state.doc.content;

        const shouldImmediatelyUpdate =
            // If the number of lines change, flush immediately
            getLineCount(previousContent) !== getLineCount(currentContent) ||
            // A task item's label chip renders from the labels registered on the owning element, which
            // are derived from the content when it saves - so we need to flush immediately to render any new labels
            haveTaskListLabelIdsChanged({ previousContent, currentContent }) ||
            // Or we've exceeded our debounce count
            this.unsyncedUpdateCount >= this.options.getDebounceCount();

        if (shouldImmediatelyUpdate) return this.flushUpdate({ editor });

        this.timeoutId = setTimeout(
            () => this.flushUpdate({ editor }),
            this.options.getDebounceTime(),
        ) as unknown as number;
    }

    /**
     * If there's a pending update - cancel it and reset the unsynced update count.
     * Another update will need to be triggered in order for the content to be synced.
     */
    public cancelPendingUpdate() {
        if (this.timeoutId) clearTimeout(this.timeoutId);
        this.unsyncedUpdateCount = 0;
        return true;
    }

    /**
     * Resets this class back to its initial state.
     */
    public cleanupExtensionStorage({ editor }: { editor: Editor }) {
        if (this.timeoutId) clearTimeout(this.timeoutId);

        this.flushUpdate({ editor });

        this.lastStoredContent = null;
        this.pausers.reset();
        this.unsyncedUpdateCount = 0;
    }

    public beforeUnloadListener: (() => void) | undefined;
}
